import { motion } from "framer-motion";
import {
  Mail,
  Activity,
  Lock,
  CheckCircle2,
  ArrowRight,
  Server,
  Gauge,
  TrendingUp,
} from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { GROWTH_PACKS } from "@/lib/stripe-config";

type Locale = "en" | "es" | "pt-br" | "de";

interface Props {
  locale?: Locale;
}

const STRINGS: Record<Locale, {
  eyebrow: string;
  heading: string;
  headingAccent: string;
  sub: string;
  step1Title: string;
  step1Body: string;
  step1Footnote: string;
  step1Chips: string[];
  step2Title: string;
  step2Body: string;
  step2Throttle: string;
  step3Title: string;
  step3Body: string;
  ladder: { stage: string; tier: string; capacity: string; gate: string }[];
  footer: string;
}> = {
  en: {
    eyebrow: "Infrastructure & performance gating",
    heading: "Infrastructure that ",
    headingAccent: "earns its volume",
    sub: "Bring your inboxes. We measure every send. Capacity unlocks only when the numbers say it should.",
    step1Title: "1. Connect your inboxes",
    step1Body: "Plug in your own Google Workspace, Microsoft 365, or custom SMTP mailboxes. We handle warm-up, DNS (SPF/DKIM/DMARC) and reply routing.",
    step1Footnote: "Every burner domain hosts up to 5 mailboxes — so 20 inboxes = 4 domains, 100 inboxes = 20 domains.",
    step1Chips: ["Google Workspace", "Microsoft 365", "Custom SMTP"],
    step2Title: "2. We measure every send",
    step2Body: "Each inbox is scored live against the four signals that actually decide outbound performance.",
    step2Throttle: "Auto-throttle below 80% placement or above 2% bounce",
    step3Title: "3. Earn more capacity",
    step3Body: "Campaigns climb a 3-stage ladder. Each stage unlocks the next ceiling of managed inboxes and burner domains — no blind volume buys.",
    ladder: [
      { stage: "Explore", tier: "Starter", capacity: "Your owned inboxes", gate: "Default — every new campaign starts here" },
      { stage: "Validate", tier: `Pack 1 · ${GROWTH_PACKS.pack1.inbox_ceiling} inboxes / ${GROWTH_PACKS.pack1.domain_ceiling} domains`, capacity: `Up to ${GROWTH_PACKS.pack1.inbox_ceiling} managed inboxes`, gate: "Unlocks at: placement ≥ 80% · bounce < 2% · positive reply rate ≥ benchmark" },
      { stage: "Scale", tier: `Pack 2 · ${GROWTH_PACKS.pack2.inbox_ceiling} inboxes / ${GROWTH_PACKS.pack2.domain_ceiling} domains`, capacity: `Up to ${GROWTH_PACKS.pack2.inbox_ceiling} managed inboxes`, gate: "Unlocks at: meeting rate ≥ benchmark on validate-stage volume" },
      { stage: "Exploit", tier: `Pack 3 · ${GROWTH_PACKS.pack3.inbox_ceiling} inboxes / ${GROWTH_PACKS.pack3.domain_ceiling} domains`, capacity: `Up to ${GROWTH_PACKS.pack3.inbox_ceiling} managed inboxes`, gate: "Unlocks at: sustained scale-stage performance for 14 days" },
    ],
    footer: "Buying a pack raises the ceiling. The allocator only provisions the next inbox once the current ones are performing — and throttles the moment they're not.",
  },
  es: {
    eyebrow: "Infraestructura y gating por rendimiento",
    heading: "Infraestructura que ",
    headingAccent: "se gana su volumen",
    sub: "Conecta tus inboxes. Medimos cada envío. La capacidad se desbloquea sólo cuando los números lo justifican.",
    step1Title: "1. Conecta tus inboxes",
    step1Body: "Conecta tus propios buzones de Google Workspace, Microsoft 365 o SMTP personalizado. Nosotros gestionamos el calentamiento, DNS (SPF/DKIM/DMARC) y enrutamiento de respuestas.",
    step1Footnote: "Cada dominio burner aloja hasta 5 buzones — así 20 inboxes = 4 dominios, 100 inboxes = 20 dominios.",
    step1Chips: ["Google Workspace", "Microsoft 365", "SMTP personalizado"],
    step2Title: "2. Medimos cada envío",
    step2Body: "Cada inbox se evalúa en vivo con las cuatro señales que realmente deciden el rendimiento outbound.",
    step2Throttle: "Throttle automático bajo 80% de entrega o sobre 2% de rebote",
    step3Title: "3. Gana más capacidad",
    step3Body: "Las campañas suben una escalera de 3 etapas. Cada etapa desbloquea el siguiente techo de inboxes gestionados y dominios burner — nada de comprar volumen a ciegas.",
    ladder: [
      { stage: "Explore", tier: "Starter", capacity: "Tus inboxes propios", gate: "Por defecto — cada campaña nueva empieza aquí" },
      { stage: "Validate", tier: `Pack 1 · ${GROWTH_PACKS.pack1.inbox_ceiling} inboxes / ${GROWTH_PACKS.pack1.domain_ceiling} dominios`, capacity: `Hasta ${GROWTH_PACKS.pack1.inbox_ceiling} inboxes gestionados`, gate: "Se desbloquea: entrega ≥ 80% · rebote < 2% · tasa de respuesta positiva ≥ benchmark" },
      { stage: "Scale", tier: `Pack 2 · ${GROWTH_PACKS.pack2.inbox_ceiling} inboxes / ${GROWTH_PACKS.pack2.domain_ceiling} dominios`, capacity: `Hasta ${GROWTH_PACKS.pack2.inbox_ceiling} inboxes gestionados`, gate: "Se desbloquea: tasa de meetings ≥ benchmark en volumen validate" },
      { stage: "Exploit", tier: `Pack 3 · ${GROWTH_PACKS.pack3.inbox_ceiling} inboxes / ${GROWTH_PACKS.pack3.domain_ceiling} dominios`, capacity: `Hasta ${GROWTH_PACKS.pack3.inbox_ceiling} inboxes gestionados`, gate: "Se desbloquea: rendimiento scale sostenido durante 14 días" },
    ],
    footer: "Comprar un pack eleva el techo. El allocator sólo provisiona el siguiente inbox cuando los actuales rinden — y throttlea en cuanto dejan de hacerlo.",
  },
  "pt-br": {
    eyebrow: "Infraestrutura e gating por performance",
    heading: "Infraestrutura que ",
    headingAccent: "ganha o próprio volume",
    sub: "Conecte suas inboxes. Medimos cada envio. A capacidade só destrava quando os números justificam.",
    step1Title: "1. Conecte suas inboxes",
    step1Body: "Conecte suas caixas Google Workspace, Microsoft 365 ou SMTP personalizado. Cuidamos do warm-up, DNS (SPF/DKIM/DMARC) e roteamento de respostas.",
    step1Footnote: "Cada domínio burner hospeda até 5 caixas — então 20 inboxes = 4 domínios, 100 inboxes = 20 domínios.",
    step1Chips: ["Google Workspace", "Microsoft 365", "SMTP personalizado"],
    step2Title: "2. Medimos cada envio",
    step2Body: "Cada inbox é avaliada ao vivo pelas quatro métricas que realmente decidem performance outbound.",
    step2Throttle: "Throttle automático abaixo de 80% de entrega ou acima de 2% de bounce",
    step3Title: "3. Ganhe mais capacidade",
    step3Body: "Campanhas sobem uma escada de 3 estágios. Cada estágio destrava o próximo teto de inboxes gerenciadas e domínios burner — nunca volume cego.",
    ladder: [
      { stage: "Explore", tier: "Starter", capacity: "Suas inboxes próprias", gate: "Padrão — toda nova campanha começa aqui" },
      { stage: "Validate", tier: `Pack 1 · ${GROWTH_PACKS.pack1.inbox_ceiling} inboxes / ${GROWTH_PACKS.pack1.domain_ceiling} domínios`, capacity: `Até ${GROWTH_PACKS.pack1.inbox_ceiling} inboxes gerenciadas`, gate: "Destrava: entrega ≥ 80% · bounce < 2% · taxa de resposta positiva ≥ benchmark" },
      { stage: "Scale", tier: `Pack 2 · ${GROWTH_PACKS.pack2.inbox_ceiling} inboxes / ${GROWTH_PACKS.pack2.domain_ceiling} domínios`, capacity: `Até ${GROWTH_PACKS.pack2.inbox_ceiling} inboxes gerenciadas`, gate: "Destrava: taxa de meetings ≥ benchmark em volume validate" },
      { stage: "Exploit", tier: `Pack 3 · ${GROWTH_PACKS.pack3.inbox_ceiling} inboxes / ${GROWTH_PACKS.pack3.domain_ceiling} domínios`, capacity: `Até ${GROWTH_PACKS.pack3.inbox_ceiling} inboxes gerenciadas`, gate: "Destrava: performance scale sustentada por 14 dias" },
    ],
    footer: "Comprar um pack levanta o teto. O allocator só provisiona a próxima inbox quando as atuais performam — e throttlea no instante em que param.",
  },
  de: {
    eyebrow: "Infrastruktur & Performance-Gating",
    heading: "Infrastruktur, die sich ",
    headingAccent: "ihr Volumen verdient",
    sub: "Verbinde deine Inboxes. Wir messen jeden Versand. Kapazität wird erst freigeschaltet, wenn die Zahlen es zulassen.",
    step1Title: "1. Inboxes verbinden",
    step1Body: "Verbinde deine eigenen Google Workspace, Microsoft 365 oder Custom-SMTP-Postfächer. Warm-up, DNS (SPF/DKIM/DMARC) und Reply-Routing übernehmen wir.",
    step1Footnote: "Jede Burner-Domain trägt bis zu 5 Postfächer — also 20 Inboxes = 4 Domains, 100 Inboxes = 20 Domains.",
    step1Chips: ["Google Workspace", "Microsoft 365", "Custom SMTP"],
    step2Title: "2. Wir messen jeden Versand",
    step2Body: "Jede Inbox wird live gegen die vier Signale gescort, die Outbound-Performance tatsächlich bestimmen.",
    step2Throttle: "Auto-Throttle unter 80% Placement oder über 2% Bounce",
    step3Title: "3. Mehr Kapazität verdienen",
    step3Body: "Kampagnen klettern eine 3-Stufen-Leiter. Jede Stufe schaltet das nächste Limit an Managed Inboxes und Burner-Domains frei — kein Blindkauf von Volumen.",
    ladder: [
      { stage: "Explore", tier: "Starter", capacity: "Deine eigenen Inboxes", gate: "Standard — jede neue Kampagne startet hier" },
      { stage: "Validate", tier: `Pack 1 · ${GROWTH_PACKS.pack1.inbox_ceiling} Inboxes / ${GROWTH_PACKS.pack1.domain_ceiling} Domains`, capacity: `Bis zu ${GROWTH_PACKS.pack1.inbox_ceiling} Managed Inboxes`, gate: "Freischaltung: Placement ≥ 80% · Bounce < 2% · Positive-Reply-Rate ≥ Benchmark" },
      { stage: "Scale", tier: `Pack 2 · ${GROWTH_PACKS.pack2.inbox_ceiling} Inboxes / ${GROWTH_PACKS.pack2.domain_ceiling} Domains`, capacity: `Bis zu ${GROWTH_PACKS.pack2.inbox_ceiling} Managed Inboxes`, gate: "Freischaltung: Meeting-Rate ≥ Benchmark auf Validate-Volumen" },
      { stage: "Exploit", tier: `Pack 3 · ${GROWTH_PACKS.pack3.inbox_ceiling} Inboxes / ${GROWTH_PACKS.pack3.domain_ceiling} Domains`, capacity: `Bis zu ${GROWTH_PACKS.pack3.inbox_ceiling} Managed Inboxes`, gate: "Freischaltung: dauerhafte Scale-Performance über 14 Tage" },
    ],
    footer: "Ein Pack zu kaufen hebt das Limit. Der Allocator stellt die nächste Inbox erst bereit, wenn die aktuellen performen — und throttled im Moment, wo sie es nicht tun.",
  },
};

const KPIS_BY_LOCALE: Record<Locale, { label: string; value: string }[]> = {
  en: [
    { label: "Inbox placement", value: "≥ 80%" },
    { label: "Bounce rate", value: "< 2%" },
    { label: "Positive reply rate", value: "≥ benchmark" },
    { label: "Meeting rate / 100 sent", value: "≥ benchmark" },
  ],
  es: [
    { label: "Entrega al inbox", value: "≥ 80%" },
    { label: "Tasa de rebote", value: "< 2%" },
    { label: "Respuesta positiva", value: "≥ benchmark" },
    { label: "Meetings / 100 envíos", value: "≥ benchmark" },
  ],
  "pt-br": [
    { label: "Entrega no inbox", value: "≥ 80%" },
    { label: "Taxa de bounce", value: "< 2%" },
    { label: "Resposta positiva", value: "≥ benchmark" },
    { label: "Meetings / 100 envios", value: "≥ benchmark" },
  ],
  de: [
    { label: "Inbox-Placement", value: "≥ 80%" },
    { label: "Bounce-Rate", value: "< 2%" },
    { label: "Positive Reply-Rate", value: "≥ Benchmark" },
    { label: "Meetings / 100 Sends", value: "≥ Benchmark" },
  ],
};

export function InfrastructureLadder({ locale = "en" }: Props) {
  const t = STRINGS[locale] ?? STRINGS.en;
  const kpis = KPIS_BY_LOCALE[locale] ?? KPIS_BY_LOCALE.en;

  return (
    <section className="border-t border-border bg-card/30">
      <div className="max-w-6xl mx-auto px-4 py-16 md:py-24 space-y-12">
        <Reveal stagger={60} className="text-center space-y-3 max-w-3xl mx-auto">
          <p className="text-[10px] font-mono uppercase tracking-widest text-primary">
            {t.eyebrow}
          </p>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground tracking-tight">
            {t.heading}
            <span className="text-gradient-brand">{t.headingAccent}</span>
          </h2>
          <p className="text-sm md:text-base text-muted-foreground max-w-xl mx-auto leading-relaxed">
            {t.sub}
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 relative">
          {/* Step 1 — Connect */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0 }}
            className="rounded-2xl border border-border bg-card p-6 space-y-4 flex flex-col"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <h3 className="text-base font-semibold text-foreground">{t.step1Title}</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{t.step1Body}</p>
            <div className="flex flex-wrap gap-2 pt-1">
              {t.step1Chips.map((chip) => (
                <span
                  key={chip}
                  className="text-[10px] font-mono uppercase tracking-wider text-foreground/75 border border-border bg-background/60 rounded-md px-2 py-1"
                >
                  {chip}
                </span>
              ))}
            </div>
            <div className="mt-auto pt-3 border-t border-border/60">
              <div className="flex items-start gap-2 text-[11px] text-muted-foreground leading-relaxed">
                <Server className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                <span>{t.step1Footnote}</span>
              </div>
            </div>
          </motion.div>

          {/* Step 2 — Measure */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.08 }}
            className="rounded-2xl border border-border bg-card p-6 space-y-4 flex flex-col"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Gauge className="h-5 w-5 text-primary" />
              </div>
              <h3 className="text-base font-semibold text-foreground">{t.step2Title}</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{t.step2Body}</p>
            <ul className="space-y-2 pt-1">
              {kpis.map((k) => (
                <li
                  key={k.label}
                  className="flex items-center justify-between rounded-lg border border-border/60 bg-background/40 px-3 py-2"
                >
                  <span className="text-xs text-foreground/80">{k.label}</span>
                  <span className="text-xs font-mono text-foreground tabular-nums">{k.value}</span>
                </li>
              ))}
            </ul>
            <div className="mt-auto pt-3 border-t border-border/60 flex items-start gap-2 text-[11px] text-muted-foreground">
              <Activity className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
              <span>{t.step2Throttle}</span>
            </div>
          </motion.div>

          {/* Step 3 — Earn */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.16 }}
            className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-card p-6 space-y-4 flex flex-col"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-primary" />
              </div>
              <h3 className="text-base font-semibold text-foreground">{t.step3Title}</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{t.step3Body}</p>
            <ol className="space-y-2 pt-1">
              {t.ladder.map((row, i) => {
                const isFirst = i === 0;
                return (
                  <li
                    key={row.stage}
                    className="rounded-lg border border-border/60 bg-background/40 px-3 py-2.5 space-y-1"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {isFirst ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" />
                        ) : (
                          <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        )}
                        <span className="text-[11px] font-mono uppercase tracking-wider text-foreground">
                          {row.stage}
                        </span>
                      </div>
                      <span className="text-[10px] font-mono text-primary text-right truncate">
                        {row.tier}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-snug pl-5">
                      {row.gate}
                    </p>
                  </li>
                );
              })}
            </ol>
          </motion.div>
        </div>

        <div className="max-w-3xl mx-auto text-center text-xs text-muted-foreground flex items-start gap-2 justify-center">
          <ArrowRight className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
          <span>{t.footer}</span>
        </div>
      </div>
    </section>
  );
}
